import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Aviso, Botao, CampoDeMoeda, CampoDeSelecao, Modal, Selo } from './ui';
import { requisicaoAutenticada } from '../servicos/http';
import { lerValorDigitado } from '../servicos/formato';
import { useExercicioVigente } from '../servicos/exercicio';
import { ErroDaApi } from '../paginas/ErroDaApi';

/**
 * RN-085 — Lançar as competências pelo valor, quando o SPED ainda não chegou.
 *
 * ## Por que existe
 *
 * O contador manda o ICMS apurado de cada mês por e-mail, e a operação parava:
 * sem arquivo não havia como calcular, e o cliente ficava sem número até
 * alguém conseguir o EFD. Aqui os doze meses entram pelo valor e o servidor
 * faz a **mesma** conta do upload — tabela de utilização, deságio, benefício.
 *
 * ## O que muda em relação ao arquivo
 *
 * Nada no resultado do mês, e tudo na prova: o SPED é documento fiscal, o
 * valor digitado é declaração de quem digitou. Por isso a tela diz que o
 * arquivo pode entrar depois e sobrescrever o mês — e por isso os componentes
 * da apuração (saldo credor, ajuste E111) ficam zerados na linha lançada.
 */

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

export interface LinhaLancada {
  readonly id: string;
  readonly competencia: string;
  readonly totalSped: number;
  readonly valueSped: number;
  readonly total: number | null;
}

/** Um item pronto para a API: competência `AAAA-MM` e o ICMS apurado. */
export interface ItemLancado {
  readonly competencia: string;
  readonly totalSped: number;
}

/** Os meses preenchidos de um ano, na forma que a API recebe. */
export function itensPreenchidos(valores: Record<string, string>, anoSped: string): ItemLancado[] {
  return MESES.map((_, indice) => {
    const chave = `${anoSped}-${String(indice + 1).padStart(2, '0')}`;
    const valor = lerValorDigitado(valores[chave] ?? '');
    return valor === null || valor <= 0 ? null : { competencia: chave, totalSped: valor };
  }).filter((item): item is ItemLancado => item !== null);
}

/**
 * Os campos do lançamento — o ano e os doze meses.
 *
 * Vivem fora do modal porque o cadastro de cliente novo pede os mesmos valores
 * numa etapa do assistente: duas cópias da grade de meses seriam duas chances
 * de uma delas passar a competência no formato errado.
 *
 * Não há seletor de regime: a tabela de utilização segue o regime do
 * **fornecedor** do crédito, não o do cliente que apura — perguntar aqui daria
 * ao operador uma escolha que não é dele.
 */
export function CamposDeValores(props: {
  readonly anoSped: string;
  readonly aoTrocarAno: (ano: string) => void;
  readonly anos: readonly number[];
  readonly valores: Record<string, string>;
  readonly aoMudarValor: (competencia: string, texto: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <CampoDeSelecao
          rotulo="Ano do SPED"
          ajuda={`Alimenta a operação de ${Number(props.anoSped) + 1} (RN-619).`}
          value={props.anoSped}
          onChange={(e) => props.aoTrocarAno(e.target.value)}
          opcoes={props.anos.map((a) => ({ valor: String(a), rotulo: String(a) }))}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {MESES.map((mes, indice) => {
          const chave = `${props.anoSped}-${String(indice + 1).padStart(2, '0')}`;
          return (
            <CampoDeMoeda
              key={chave}
              rotulo={`${mes} de ${props.anoSped}`}
              value={props.valores[chave] ?? ''}
              onChange={(texto) => props.aoMudarValor(chave, texto)}
            />
          );
        })}
      </div>
    </div>
  );
}

export function LancamentoDeValores(props: {
  readonly clienteId: string;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  /** Ano da operação em foco; o SPED lançado é o do ano anterior (RN-619). */
  readonly anoDaOperacao?: number;
}): JSX.Element {
  const queryClient = useQueryClient();
  const vigente = useExercicioVigente();
  const operacao = props.anoDaOperacao ?? vigente;
  const [anoSped, setAnoSped] = useState(String(operacao - 1));
  const [valores, setValores] = useState<Record<string, string>>({});

  const lancar = useMutation({
    mutationFn: (itens: readonly { competencia: string; totalSped: number }[]) =>
      requisicaoAutenticada<LinhaLancada[]>('/icms/speds/manual', {
        metodo: 'POST',
        corpo: { clienteId: props.clienteId, itens },
      }),
    onSuccess: () => {
      // O lançamento cria cálculo como o upload cria: a apuração, o painel do
      // cliente e a carteira mudam juntos.
      void queryClient.invalidateQueries({ queryKey: ['speds', props.clienteId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-cliente', props.clienteId] });
      void queryClient.invalidateQueries({ queryKey: ['carteira'] });
      void queryClient.invalidateQueries({ queryKey: ['visao-geral'] });
    },
  });

  const preenchidos = useMemo(() => itensPreenchidos(valores, anoSped), [valores, anoSped]);

  const fechar = (): void => {
    lancar.reset();
    setValores({});
    props.aoFechar();
  };

  const resultado = lancar.data;
  const anos = [operacao - 1, operacao - 2, operacao - 3];

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Lançar valores sem arquivo"
      descricao="Informe o ICMS apurado de cada mês; o cálculo do crédito é o mesmo do envio de SPED."
      largura="larga"
      rodape={
        resultado === undefined ? (
          <>
            <Botao variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              disabled={preenchidos.length === 0}
              carregando={lancar.isPending}
              onClick={() => lancar.mutate(preenchidos)}
            >
              {preenchidos.length === 0
                ? 'Lançar'
                : `Lançar ${preenchidos.length} ${preenchidos.length === 1 ? 'mês' : 'meses'}`}
            </Botao>
          </>
        ) : (
          <Botao onClick={fechar}>Concluir</Botao>
        )
      }
    >
      {resultado === undefined ? (
        <div className="flex flex-col gap-5">
          <CamposDeValores
            anoSped={anoSped}
            aoTrocarAno={setAnoSped}
            anos={anos}
            valores={valores}
            aoMudarValor={(chave, texto) => setValores((atual) => ({ ...atual, [chave]: texto }))}
          />

          <Aviso tom="informacao" titulo="O arquivo continua valendo mais">
            Meses em branco ficam de fora. Quando o SPED chegar, envie-o normalmente: ele
            sobrescreve o mês com os valores da apuração, que é documento fiscal.
          </Aviso>

          {lancar.isError && (
            <ErroDaApi titulo="Não foi possível lançar os valores" erro={lancar.error} />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Aviso tom="positivo" titulo={`${resultado.length} competência(s) calculada(s)`}>
            Os meses entraram na apuração deste cliente e já aparecem nos relatórios.
          </Aviso>

          <ul className="flex flex-col gap-2">
            {resultado.map((linha) => (
              <li
                key={linha.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-borda px-3 py-2 text-sm"
              >
                <span className="valor-fiscal text-conteudo-primario">{linha.competencia}</span>
                <span className="flex items-center gap-3 text-xs text-conteudo-secundario">
                  <span>
                    apurado{' '}
                    <span className="valor-fiscal">
                      {linha.totalSped.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </span>
                  </span>
                  <Selo tom="positivo" tamanho="pequeno">
                    crédito{' '}
                    {linha.valueSped.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </Selo>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
